import { snapshot as authSnapshot } from "@internal/auth";
import type { DayKey } from "@/lib/days";
import { getDb, type PlannerDoc } from "@/lib/db";
import {
  weekId as buildWeekId,
  type SettingsData,
  WEEK_TYPE,
  type WeekData,
  type WidgetConfig,
  type WidgetWeekEntry,
} from "@/lib/schema";
import { buildWeekEntry, reconcileWeekWidgets } from "@/lib/week-updates";

function currentUserId(): string {
  const user = authSnapshot().user;
  if (!user) throw new Error("Not signed in");
  return user.id;
}

interface WeekRecord {
  id: string;
  data: WeekData;
}

function snapshotWidgets(widgets: WidgetConfig[]): WidgetWeekEntry[] {
  // No previous entry, so every widget gets empty runtime — the same builder
  // an update uses when it carries runtime forward.
  return widgets.map((w) => buildWeekEntry(w));
}

interface CreateWeekOpts {
  weekNumber: number;
  year: number;
  startDate: string;
  settings: SettingsData;
}

/** Refused creation: another week, open or completed, holds this ISO week. */
class WeekTakenError extends Error {
  constructor(readonly weekNumber: number) {
    super(`Uke ${weekNumber} finnes allerede.`);
    this.name = "WeekTakenError";
  }
}

export async function createWeek(opts: CreateWeekOpts): Promise<WeekRecord> {
  const userId = currentUserId();
  const db = await getDb();
  // At most one week per ISO week (CONTEXT.md, Week). The caller's list may
  // be stale — a double click, another tab — so check the store itself.
  const existing = await db.daily_planner
    .find({ selector: { user_id: userId, type: WEEK_TYPE } })
    .exec();
  const taken = existing.some((doc) => {
    const week = (doc.toJSON() as PlannerDoc).data as unknown as WeekData;
    return week.year === opts.year && week.weekNumber === opts.weekNumber;
  });
  if (taken) throw new WeekTakenError(opts.weekNumber);
  const id = buildWeekId(crypto.randomUUID());
  const data: WeekData = {
    weekNumber: opts.weekNumber,
    year: opts.year,
    startDate: opts.startDate,
    state: "open",
    widgets: snapshotWidgets(opts.settings.widgets),
    completedDays: {},
  };
  await db.daily_planner.insert({
    id,
    type: WEEK_TYPE,
    user_id: userId,
    data: data as unknown as Record<string, unknown>,
  });
  return { id, data };
}

const writeQueues = new Map<string, Promise<unknown>>();
function enqueue<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeQueues.get(key) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  writeQueues.set(
    key,
    next.catch(() => {}),
  );
  return next;
}

export async function updateWeek(
  id: string,
  patch: (prev: WeekData) => WeekData,
): Promise<void> {
  const db = await getDb();
  await enqueue(id, async () => {
    const doc = await db.daily_planner.findOne(id).exec();
    if (!doc) throw new Error(`Week ${id} not found`);
    const prev = (doc.toJSON() as PlannerDoc).data as unknown as WeekData;
    const next = patch(prev);
    await doc.patch({ data: next as unknown as Record<string, unknown> });
  });
}

export async function completeWeek(id: string): Promise<void> {
  await updateWeek(id, (prev) => ({ ...prev, state: "completed" }));
}

/**
 * Bring one week's snapshot up to date with the mal (docs/adr/0014). The
 * caller has already shown the user what this costs; nothing here asks again.
 * `completedDays` is deliberately untouched — a day the user closed stays
 * closed even when a widget arrives in it unlogged.
 */
async function applyMalToWeek(
  id: string,
  widgets: WidgetConfig[],
): Promise<void> {
  await updateWeek(id, (prev) => ({
    ...prev,
    widgets: reconcileWeekWidgets(prev.widgets, widgets),
  }));
}

export async function applyMalToWeeks(
  ids: string[],
  widgets: WidgetConfig[],
): Promise<void> {
  for (const id of ids) await applyMalToWeek(id, widgets);
}

export async function setDayCompleted(
  id: string,
  dayKey: DayKey,
  completed: boolean,
): Promise<void> {
  await updateWeek(id, (prev) => ({
    ...prev,
    completedDays: { ...(prev.completedDays ?? {}), [dayKey]: completed },
  }));
}

export async function reopenWeek(id: string): Promise<void> {
  await updateWeek(id, (prev) => ({ ...prev, state: "open" }));
}

export async function deleteWeek(id: string): Promise<void> {
  const db = await getDb();
  const doc = await db.daily_planner.findOne(id).exec();
  if (!doc) return;
  await doc.remove();
}

export async function findWeekByYearAndNumber(
  year: number,
  weekNumber: number,
): Promise<WeekRecord | null> {
  const userId = currentUserId();
  const db = await getDb();
  const docs = await db.daily_planner
    .find({ selector: { user_id: userId, type: WEEK_TYPE } })
    .exec();
  for (const doc of docs) {
    const json = doc.toJSON() as PlannerDoc;
    const data = json.data as unknown as WeekData;
    if (data.year === year && data.weekNumber === weekNumber) {
      return { id: json.id, data };
    }
  }
  return null;
}
